import { getMockDb, markDirty } from "@/mock-api/db.ts";
import type { LocationSpecificData } from "@/mock-api/defaultDb.ts";
import { getMockTimeTz } from "@/mock-api/mockTime.ts";
import { http } from "@/mock-api/msw/handlerMiddleware.ts";
import type { StandardRequest } from "@/mock-api/msw/StandardRequest.ts";
import { mustFindBy } from "@/mock-api/mustFindBy.ts";
import {
    generateId,
    getLocationDb,
    getUserId,
    parseJson,
    throwStandardErrorResponse,
    validateSpec,
} from "@/mock-api/requestUtils.ts";
import type {
    AdminMetalHistoryItem,
    AdminMetalHistoryList,
    AdminMetalMachineItem,
    AdminMetalMachineList,
    AdminMetalSpecList,
    MetalCommitmentOfferResponse,
    MetalCommitmentResponse,
    MetalLease,
    MetalListItem,
    MetalListResponse,
    MetalMachine,
    MetalOs,
    MetalOsListResponse,
    MetalOsType,
    MetalSpec,
    MetalSpecListItem,
    MetalSpecListResponse,
    MetalStateHistory,
} from "@warrenio/api-spec/spec.oats.gen";
import * as apiSpec from "@warrenio/api-spec/spec/metal";
import { MetalOsId, MetalOsTypeId } from "@warrenio/api-spec/spec/metal";
import * as adminSpec from "@warrenio/api-spec/spec/metal.admin";
import { filterNulls } from "@warrenio/utils/collections/filterNulls";
import { notNull } from "@warrenio/utils/notNull";
import { randomUUID } from "@warrenio/utils/ponyfill/randomUUID";
import type { HttpHandler } from "msw";
import { HttpResponse } from "msw";
import { pick } from "remeda";
import invariant from "tiny-invariant";

/* eslint-disable @typescript-eslint/require-await */

/** Joins all the metal entities together, starting from machine */
function metalJoin(db: LocationSpecificData) {
    const globalDb = getMockDb();
    return db.metalMachines.map((machine) => {
        const state = notNull(db.metalStates.find((state) => state.state_id === machine.current_state_id));
        const lease = state.lease_id ? db.metalLeases.find((lease) => lease.lease_id === state.lease_id) : undefined;
        const spec = notNull(db.metalSpecs.find((spec) => spec.spec_id === machine.spec_id));
        const os = state.os_id ? globalDb.metalOs.find((os) => os.os_id === state.os_id) : undefined;
        const os_type = os ? globalDb.metalOsTypes.find((osType) => osType.os_type_id === os.os_type_id) : undefined;
        return {
            machine,
            lease,
            state,
            spec,
            os,
            os_type,
        };
    });
}

function isAvailable(state: MetalStateHistory) {
    return state.status === "available";
}

/** Convert internal entities to user-visible format */
function buildItem(m: {
    machine: MetalMachine;
    lease: MetalLease | undefined;
    state: MetalStateHistory;
    spec: MetalSpec;
}): MetalListItem {
    invariant(m.lease);
    return {
        ...m.lease,
        spec: pick(m.spec, ["spec_id", "uuid", "title", "subtitle", "description"]),
        os_id: m.state.os_id,
        billing_account_id: notNull(m.state.billing_account_id),
        created_at: m.lease.created_at,
        ip_public_v4: m.machine.ip_public_v4,
        ip_public_v6: m.machine.ip_public_v6,
        mac_addresses: m.machine.mac_addresses,
        status: apiSpec.MetalLeasedStatus.parse(m.state.status),
        ssh_credentials: m.machine.ssh_credentials,
    };
}

function changeState(db: LocationSpecificData, machine: MetalMachine, newState: MetalStateHistory) {
    validateSpec(apiSpec.MetalStateHistory, newState);
    db.metalStates.push(newState);

    // NB: Update current state ID
    machine.current_state_id = newState.state_id;

    markDirty();
}

export const metalHandlers: HttpHandler[] = [
    //#region User
    http.get("/{location}/metal/commitments", async (r) => {
        return HttpResponse.json<MetalCommitmentResponse>(getLocationDb(r).metalCommitments);
    }),

    http.post("/{location}/metal/commitments", async (r) => {
        const { billing_account_id, resource_request } = await parseJson(r, apiSpec.MetalCommitmentCreateBody);

        invariant(resource_request);
        invariant(resource_request.type === "new_metal_lease");

        const db = getLocationDb(r);
        const globalDb = getMockDb();

        const _account = mustFindBy(globalDb.billingAccounts, (a) => a.id, billing_account_id, "billing account");

        const spec = mustFindBy(db.metalSpecs, (s) => s.uuid, resource_request.machine_spec_uuid, "spec");

        const os = mustFindBy(globalDb.metalOs, (o) => o.os_id, resource_request.requested_os_id, "OS");

        const availableMachines = metalJoin(db).filter((m) => m.spec.spec_id === spec.spec_id && isAvailable(m.state));

        if (availableMachines.length === 0) {
            throwStandardErrorResponse("No available machines");
        }

        const { machine, state: currentState } =
            availableMachines.find((m) => m.os?.os_id === os.os_id) ?? availableMachines[0];
        const isReadyNow = currentState.os_id === os.os_id;

        const created_at = getMockTimeTz();
        const lease: MetalLease = {
            lease_id: generateId(),
            uuid: randomUUID(),

            created_at,
            updated_at: created_at,

            user_id: getUserId(r),
            machine_id: machine.machine_id,

            requested_os_id: os.os_id,
            display_name: resource_request.display_name,
        };
        validateSpec(apiSpec.MetalLease, lease);

        const newState: MetalStateHistory = {
            ...makeNewState(r, machine),

            action: "user_request",

            billing_account_id,
            os_id: os.os_id,

            lease_id: lease.lease_id,
            status: isReadyNow ? "in_use" : "pending",
        };
        changeState(db, machine, newState);

        // NB: Push lease after state so DB is not modified if the state change fails
        db.metalLeases.push(lease);
        markDirty();

        return HttpResponse.json<MetalListItem>(buildItem({ machine, lease, state: newState, spec }));
    }),

    http.get("/{location}/metal/commitment_offers", async (r) => {
        return HttpResponse.json<MetalCommitmentOfferResponse>(getLocationDb(r).metalCommitmentOffers);
    }),

    http.get("/{location}/metal/commitment_offers/{uuid}", async (r) => {
        return HttpResponse.json<MetalCommitmentOfferResponse>(getLocationDb(r).metalCommitmentOffers);
    }),

    http.get("/{location}/admin/metal/commitments", async (r) => {
        return HttpResponse.json<MetalCommitmentResponse>(getLocationDb(r).metalCommitments);
    }),

    http.get("/{location}/admin/metal/commitment_offers/{uuid}", async (r) => {
        return HttpResponse.json<MetalCommitmentOfferResponse>(getLocationDb(r).metalCommitmentOffers);
    }),

    http.put("/{location}/admin/metal/commitment_offers/{uuid}", async (r) => {
        const offers = await parseJson(r, apiSpec.MetalCommitmentOfferResponse);
        getLocationDb(r).metalCommitmentOffers = offers;
        markDirty();
        return HttpResponse.json<MetalCommitmentOfferResponse>(offers);
    }),

    http.get("/{location}/metal/leases", async (r) => {
        const userId = getUserId(r);
        const items = metalJoin(getLocationDb(r))
            .filter((m) => m.lease?.user_id === userId)
            .map((m) => buildItem(m));

        return HttpResponse.json<MetalListResponse>(filterNulls(items));
    }),

    http.get("/metal/os", async (_r) => {
        return HttpResponse.json<MetalOsListResponse>({
            os: getMockDb().metalOs,
            os_types: getMockDb().metalOsTypes,
        });
    }),

    http.get("/{location}/metal/specs", async (r) => {
        const db = getLocationDb(r);
        const specs = db.metalSpecs
            .filter((s) => s.is_visible)
            .map((s): MetalSpecListItem => {
                const available = db.metalMachines
                    .filter((m) => m.spec_id === s.spec_id)
                    .map((m) => notNull(db.metalStates.find((st) => st.state_id === m.current_state_id)))
                    .filter((st) => isAvailable(st));

                return {
                    ...s,
                    is_available: available.length > 0,
                    ready_os_ids: filterNulls(available.map((st) => st.os_id)),
                };
            });

        return HttpResponse.json<MetalSpecListResponse>(specs);
    }),

    http.post("/{location}/metal/leases", async (r) => {
        const { billing_account_id, display_name, requested_os_id, spec_uuid } = await parseJson(
            r,
            apiSpec.MetalLeaseCreateBody,
        );

        const db = getLocationDb(r);
        const globalDb = getMockDb();

        const _account = mustFindBy(globalDb.billingAccounts, (a) => a.id, billing_account_id, "billing account");

        const spec = mustFindBy(db.metalSpecs, (s) => s.uuid, spec_uuid, "spec");

        const os = mustFindBy(globalDb.metalOs, (o) => o.os_id, requested_os_id, "OS");

        const availableMachines = metalJoin(db).filter((m) => m.spec.spec_id === spec.spec_id && isAvailable(m.state));

        if (availableMachines.length === 0) {
            throwStandardErrorResponse("No available machines");
        }

        const { machine, state: currentState } =
            availableMachines.find((m) => m.os?.os_id === os.os_id) ?? availableMachines[0];
        const isReadyNow = currentState.os_id === os.os_id;

        const created_at = getMockTimeTz();
        const lease: MetalLease = {
            lease_id: generateId(),
            uuid: randomUUID(),

            created_at,
            updated_at: created_at,

            user_id: getUserId(r),
            machine_id: machine.machine_id,

            requested_os_id: os.os_id,
            display_name,
        };
        validateSpec(apiSpec.MetalLease, lease);

        const newState: MetalStateHistory = {
            ...makeNewState(r, machine),

            action: "user_request",

            billing_account_id,
            os_id: os.os_id,

            lease_id: lease.lease_id,
            status: isReadyNow ? "in_use" : "pending",
        };
        changeState(db, machine, newState);

        // NB: Push lease after state so DB is not modified if the state change fails
        db.metalLeases.push(lease);
        markDirty();

        return HttpResponse.json<MetalListItem>(buildItem({ machine, lease, state: newState, spec }));
    }),

    http.patch("/{location}/metal/leases/{uuid}", async (r) => {
        const { uuid } = r.params;
        const rest = await parseJson(r, apiSpec.MetalLeaseUpdateBody);
        const lease = mustFindBy(getLocationDb(r).metalLeases, (l) => l.uuid, uuid, "lease");

        Object.assign(lease, rest);
        lease.updated_at = getMockTimeTz();

        // TODO: Check invariants

        return HttpResponse.json({ success: true });
    }),

    http.delete("/{location}/metal/leases/{uuid}", async (r) => {
        const { uuid } = r.params;
        const db = getLocationDb(r);

        const lease = notNull(db.metalLeases.find((l) => l.uuid === uuid));
        const state = notNull(db.metalStates.find((s) => s.machine_id === lease.machine_id));
        const machine = notNull(db.metalMachines.find((m) => m.machine_id === lease.machine_id));

        // TODO: Check invariants

        const newState: MetalStateHistory = {
            ...makeNewState(r, machine),

            action: "user_release",

            billing_account_id: state.billing_account_id,
            os_id: state.os_id,

            lease_id: null,
            status: "cleaning",
        };
        changeState(db, machine, newState);

        return HttpResponse.json({ success: true });
    }),
    //#endregion

    //#region Admin

    /// Machines
    http.get("/{location}/admin/metal/machines", async (r) => {
        return HttpResponse.json<AdminMetalMachineList>(
            metalJoin(getLocationDb(r)).map(
                (m): AdminMetalMachineItem => ({
                    ...m.machine,
                    ...pick(m.state, ["os_id", "status", "lease_id", "billing_account_id"]),
                    lease: m.lease,
                    spec: m.spec,
                }),
            ),
        );
    }),

    http.post("/{location}/admin/metal/machines", async (r) => {
        const { os_id, ...body } = await parseJson(r, adminSpec.AdminMetalMachineCreateBody);

        const db = getLocationDb(r);
        const spec = mustFindBy(db.metalSpecs, (s) => s.spec_id, body.spec_id, "spec");

        const machine: MetalMachine = {
            ...body,
            machine_id: generateId(),
            uuid: randomUUID(),
            created_at: getMockTimeTz(),
            spec_id: spec.spec_id!,
            current_state_id: undefined!,
        };
        db.metalMachines.push(machine);
        markDirty();

        const state: MetalStateHistory = {
            ...makeNewState(r, machine),

            action: "admin_create",
            billing_account_id: null,
            os_id,
            lease_id: null,
            status: "offline",
        };
        changeState(db, machine, state);

        return HttpResponse.json<MetalMachine>(machine);
    }),
    http.patch("/{location}/admin/metal/machines/{uuid}", async (r) => {
        const { uuid } = r.params;
        const { os_id, status, ...body } = await parseJson(r, adminSpec.AdminMetalMachineUpdateBody);

        const db = getLocationDb(r);
        const machine = mustFindBy(db.metalMachines, (m) => m.uuid, uuid, "machine");
        const state = mustFindBy(db.metalStates, (s) => s.state_id, machine.current_state_id, "state");

        // Update machine fields
        Object.assign(machine, body);
        markDirty();

        // Update state if necessary
        if (status !== state.status || os_id !== state.os_id) {
            const newStatus = status ?? state.status;
            const isLeasedStatus = newStatus === "in_use" || newStatus === "pending";

            // Release lease if status changes to non-leased
            const newLeaseId = isLeasedStatus ? state.lease_id : null;
            const newBaId = isLeasedStatus ? state.billing_account_id : null;

            const newState: MetalStateHistory = {
                ...makeNewState(r, machine),

                action: "admin_update",
                billing_account_id: newBaId,
                os_id: os_id ?? state.os_id,
                lease_id: newLeaseId,
                status: newStatus,
            };
            // TODO: Invariants
            changeState(db, machine, newState);
        }

        return HttpResponse.json<MetalMachine>(machine);
    }),
    http.delete("/{location}/admin/metal/machines/{uuid}", async (r) => {
        const { uuid } = r.params;
        const db = getLocationDb(r);
        const machine = mustFindBy(db.metalMachines, (m) => m.uuid, uuid, "machine");

        const newState: MetalStateHistory = {
            ...makeNewState(r, machine),

            action: "admin_delete",
            billing_account_id: null,
            os_id: null,
            lease_id: null,
            status: "deleted",
        };
        changeState(db, machine, newState);

        return HttpResponse.json({ success: true });
    }),

    http.post("/{location}/admin/metal/leases", async (r) => {
        const { billing_account_id, display_name, machine_uuid, requested_os_id } = await parseJson(
            r,
            adminSpec.AdminMetalLeaseCreateBody,
        );

        const db = getLocationDb(r);
        const globalDb = getMockDb();

        const ba = mustFindBy(globalDb.billingAccounts, (a) => a.id, billing_account_id, "billing account");

        const machine = mustFindBy(db.metalMachines, (m) => m.uuid, machine_uuid, "machine");
        const os = mustFindBy(globalDb.metalOs, (o) => o.os_id, requested_os_id, "OS");
        const state = mustFindBy(db.metalStates, (s) => s.machine_id, machine.machine_id, "state");

        // TODO: Check invariants

        const created_at = getMockTimeTz();
        const lease: MetalLease = {
            lease_id: generateId(),
            uuid: randomUUID(),

            created_at,
            updated_at: created_at,

            user_id: ba.user_id,
            machine_id: machine.machine_id,
            requested_os_id: os.os_id,
            display_name,
        };
        validateSpec(apiSpec.MetalLease, lease);

        const newState: MetalStateHistory = {
            ...makeNewState(r, machine),

            action: "admin_lease_create",
            billing_account_id,
            os_id: state.os_id,

            lease_id: lease.lease_id,
            status: "pending",
        };
        changeState(db, machine, newState);

        db.metalLeases.push(lease);
        markDirty();

        return HttpResponse.json<MetalLease>(lease);
    }),

    http.patch("/{location}/admin/metal/leases/{uuid}", async (r) => {
        // Does not allow changing any `state` fields currently
        const { uuid } = r.params;
        const body = await parseJson(r, adminSpec.AdminMetalLeaseUpdateBody);

        const db = getLocationDb(r);
        const lease = mustFindBy(db.metalLeases, (l) => l.uuid, uuid, "lease");

        Object.assign(lease, body);
        lease.updated_at = getMockTimeTz();
        markDirty();

        return HttpResponse.json<MetalLease>(lease);
    }),

    /// Machines history
    http.get("/{location}/admin/metal/machines/{uuid}/history", async (r) => {
        const { uuid } = r.params;
        const db = getLocationDb(r);
        const machine = mustFindBy(db.metalMachines, (m) => m.uuid, uuid, "machine");

        const history = db.metalStates
            .filter((s) => s.machine_id === machine.machine_id)
            .map(
                (s): AdminMetalHistoryItem => ({
                    ...s,
                    lease: s.lease_id ? db.metalLeases.find((l) => l.lease_id === s.lease_id) : undefined,
                }),
            );

        return HttpResponse.json<AdminMetalHistoryList>(history);
    }),

    /// Specs
    http.get("/{location}/admin/metal/specs", async (r) => {
        return HttpResponse.json<AdminMetalSpecList>(getLocationDb(r).metalSpecs);
    }),

    http.post("/{location}/admin/metal/specs", async (r) => {
        const body = await parseJson(r, adminSpec.AdminMetalSpecCreateBody);

        const created_at = getMockTimeTz();
        const spec: MetalSpec = {
            ...body,
            spec_id: generateId(),
            uuid: randomUUID(),
            created_at,
            updated_at: created_at,
            is_deleted: false,
        };
        getLocationDb(r).metalSpecs.push(spec);
        markDirty();

        return HttpResponse.json<MetalSpec>(spec);
    }),
    http.patch("/{location}/admin/metal/specs/{uuid}", async (r) => {
        const { uuid } = r.params;
        const body = await parseJson(r, adminSpec.AdminMetalSpecUpdateBody);

        const spec = mustFindBy(getLocationDb(r).metalSpecs, (s) => s.uuid, uuid, "spec");
        Object.assign(spec, body);
        spec.updated_at = getMockTimeTz();
        markDirty();

        return HttpResponse.json<MetalSpec>(spec);
    }),
    http.delete("/{location}/admin/metal/specs/{uuid}", async (r) => {
        const { uuid } = r.params;
        const spec = mustFindBy(getLocationDb(r).metalSpecs, (s) => s.uuid, uuid, "spec");

        // TODO: invariants

        spec.is_deleted = true;
        markDirty();

        return HttpResponse.json({ success: true });
    }),
    //#endregion

    //#region Operating systems & types
    http.post("/admin/metal/os", async (r) => {
        const body = await parseJson(r, apiSpec.MetalOsCreate);

        const os: MetalOs = {
            ...body,
            is_deleted: false,
            updated_at: getMockTimeTz(),
        };
        getMockDb().metalOs.push(os);
        markDirty();

        return HttpResponse.json(os);
    }),
    http.patch("/admin/metal/os/{os_id}", async (r) => {
        const os_id = MetalOsId.parse(r.params.os_id);
        const body = await parseJson(r, apiSpec.MetalOsUpdate);

        const os = mustFindBy(getMockDb().metalOs, (o) => o.os_id, os_id, "OS");
        Object.assign(os, body);
        os.updated_at = getMockTimeTz();
        markDirty();

        return HttpResponse.json(os);
    }),
    http.delete("/admin/metal/os/{os_id}", async (r) => {
        const os_id = MetalOsId.parse(r.params.os_id);

        getMockDb().metalOs = getMockDb().metalOs.filter((o) => o.os_id !== os_id);
        markDirty();

        return HttpResponse.json({ success: true });
    }),

    // --- OS types ---
    http.post("/admin/metal/os_types", async (r) => {
        const body = await parseJson(r, apiSpec.MetalOsTypeCreate);

        const osType: MetalOsType = {
            ...body,
            is_deleted: false,
            updated_at: getMockTimeTz(),
        };
        getMockDb().metalOsTypes.push(osType);
        markDirty();

        return HttpResponse.json(osType);
    }),
    http.patch("/admin/metal/os_types/{os_type_id}", async (r) => {
        const os_type_id = MetalOsTypeId.parse(r.params.os_type_id);
        const body = await parseJson(r, apiSpec.MetalOsTypeUpdate);

        const osType = mustFindBy(getMockDb().metalOsTypes, (o) => o.os_type_id, os_type_id, "OS type");
        Object.assign(osType, body);
        osType.updated_at = getMockTimeTz();
        markDirty();

        return HttpResponse.json(osType);
    }),
    http.delete("/admin/metal/os_types/{os_type_id}", async (r) => {
        const os_type_id = MetalOsTypeId.parse(r.params.os_type_id);

        getMockDb().metalOsTypes = getMockDb().metalOsTypes.filter((o) => o.os_type_id !== os_type_id);
        markDirty();

        return HttpResponse.json({ success: true });
    }),
    //#endregion
];

function makeNewState(r: StandardRequest, machine: MetalMachine) {
    return {
        state_id: generateId(),
        changed_at: getMockTimeTz(),
        acting_user_id: getUserId(r),
        machine_id: machine.machine_id!,
    } satisfies Partial<MetalStateHistory>;
}
